// React
import { Fragment, useContext, useEffect, useState } from "react"

// Components
import { CancelButton, Card, CommandButton, CustomPrintHover, Loading, PrintTransactionModal } from "components"
import { AttachmentModal, DeleteModal, InputSection, Journal } from "./components"

// Contexts
import { DataContext, ParamsContext } from "./contexts"

// Form
import { FormProvider, useForm, useFormContext } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"

// Third-Party Libraries
import moment from "moment"
import toast from "react-hot-toast"
import { useNavigate, useParams } from "react-router-dom"

// Utils
import { convertNumber, useApi, useCountryById, useHandleAuth, useProvinceById, useRegencyById, useSubdistrictById, useTaxCode, useToggle } from "utils"
import { useList } from "pages/Setup/Preference/SettingCompany/utils/hooks"
import { useSupplier } from "../components/Supplier/utils"
import { useEmployeeDetail, useSubdomainSetting, useVendorDetail } from "../PurchaseOrder/utils"
import { useProduct } from "../PurchaseOrder/components/DetailBody/utils"
import { type FormType, useDetailReject, validationSchema, getSubtotalItem } from "./utils"
import { useApprovalStore } from "../utils"

export default function Edit(): JSX.Element {
  // Hooks
  const { bill_id, reject_id } = useParams()
  const { data, isLoading } = useDetailReject(bill_id!, reject_id!)
  const { setApproval, resetApproval } = useApprovalStore()

  useEffect(() => {
    resetApproval()
    setApproval(Number(data?.data.approve_status_id) || 0)
    return () => resetApproval()

  }, [data, setApproval, resetApproval])

  if (isLoading || !data) {
    return <Loading errorText="No data available" loading={isLoading} />
  }



  return (
    <DataContext.Provider value={data}>
      <FormSection />
    </DataContext.Provider>
  )
}

function FormSection() {
  // Hooks
  const api = useApi()
  const navigate = useNavigate()
  const { bill_id, reject_id } = useParams()
  const { data, detail_reject, attachment } = useContext(DataContext)

  // Form
  const defaultValues: FormType = {
    trx_code: data.trx_code_reject,
    transaction_date: data.transaction_date,
    memo: data.memo,
    referensi: data.referensi,
    prorejectdetail: detail_reject,
    imgPath: attachment
  }
  const methods = useForm<FormType>({
    defaultValues,
    resolver: yupResolver(validationSchema)
  })
  const onSubmit = (value: FormType) => {
    // Vars
    const finalValue = {
      ...value,
      bill_id,
      reject_id,
      payment_term_vendor_id: data.payment_term_vendor_id,
      vendor_id: data.vendor_id,
      location_id: data.location_id,
      address: data.address,
      imgPath: value.imgPath || [],
      prorejectdetail: value.prorejectdetail.map((item) => {
        // Vars
        const quantity_reject = item.quantity_reject
        const quantity_unit: number = quantity_reject * item.quantity_rate
        const subtotal: number = quantity_reject * item.price_reject
        const total: number = subtotal - (item.discount / 100) * subtotal
        const total_tax_paid: number = (item.tax_rate / 100) * total

        return {
          quantity_reject,
          quantity_unit,
          total,
          total_tax_paid,
          product_id: item.product_id,
          price_reject: item.price_reject,
          discount: item.discount,
          tax_id: item.tax_id,
          tax_rate: item.tax_rate,
          reject_detail_id: item.reject_detail_id ?? undefined
        }
      })
    }

    return new Promise<void>((resolve) => {
      toast
        .promise(api.put("/proreject/edit", finalValue), {
          loading: "Loading...",
          success: (res) => res.data.message,
          error: (err) =>
            err.response.data.detail?.message ?? err.response.data.message
        })
        .then(() => {
          navigate(-1)
        })
        .catch(() => { })
        .finally(resolve)
    })
  }

  return (
    <FormProvider {...methods}>
      <ParamsContext.Provider value={{}}>
        <InputSection
          isEdit={true}
          actionbutton={
            <Card>
              <Card.Body className="flex flex-col gap-5">
                <section className="flex flex-wrap justify-between gap-3">
                  <section className="flex flex-wrap gap-3">
                    {/* {data.approve_status === "Approved" ? ( */}
                    <Print />
                    {/* ) : (
                      <CommandButton actiontype="print" />
                    )} */}


                    <Journal />
                    <AttachmentModal
                      code={methods?.control?._formValues?.trx_code!}
                    />
                  </section>

                  <section className="flex flex-wrap gap-3">
                    <CancelButton />

                    {data.approve_status === "Pending" && <Delete />}

                    {data.approve_status !== "Approved" && (
                      <CommandButton
                        actiontype="save"
                        loading={
                          methods.formState.isSubmitting ? "true" : undefined
                        }
                        onClick={methods.handleSubmit(onSubmit)}
                      />
                    )}
                  </section>
                </section>
              </Card.Body>
            </Card>
          }
        />
      </ParamsContext.Provider>
    </FormProvider>
  )
}

function Delete(): JSX.Element {
  // Hooks
  const navigate = useNavigate()
  const { bill_id, reject_id } = useParams()
  const { isActive, toggle } = useToggle(false)

  return (
    <Fragment>
      <CommandButton actiontype="delete" onClick={toggle} />

      {isActive && (
        <DeleteModal
          bill_id={bill_id!}
          reject_id={reject_id!}
          toggle={toggle}
          onSuccess={() => navigate(-1)}
        />
      )}
    </Fragment>
  )
}

function Print(): JSX.Element {
  // Form
  const {
    formState: { defaultValues }
  } = useFormContext<FormType>()

  // Hooks
  const { data } = useContext(DataContext)
  const { isActive, toggle } = useToggle(false)
  const [withImagePrint, setWithImagePrint] = useState<boolean>(false)

  // Vars
  const vendor_id = data.vendor_id
  const product = useProduct()
  const setting = useSubdomainSetting()
  const taxCode = useTaxCode()
  const vendor = useVendorDetail(vendor_id)
  const approvedBy = useEmployeeDetail(data.approve_by)
  const company = useList()
  const country = useCountryById()
  const province = useProvinceById()
  const regency = useRegencyById()
  const subdistrict = useSubdistrictById()
  const supplier = useSupplier()
  const { userTokenData } = useHandleAuth()

  // Vars
  const [address, setAddress] = useState<string>("")
  const detail = defaultValues?.prorejectdetail ?? []
  const subtotal: number = detail.reduce((acc, item): number => {
    return acc + getSubtotalItem(item?.discount ?? 0, item?.price_reject ?? 0, item?.quantity_reject ?? 0)
  }, 0)
  const tax = detail.reduce((acc, item) => {
    return acc + (getSubtotalItem(item?.discount ?? 0, item?.price_reject ?? 0, item?.quantity_reject ?? 0) * (item?.tax_rate ?? 0) / 100)
  }, 0)
  const total = subtotal + tax
  const userToken = userTokenData()
  const isLoading: boolean = Boolean(
    approvedBy.isLoading ||
    company.isLoading ||
    country.isLoading ||
    product.isLoading ||
    province.isLoading ||
    regency.isLoading ||
    setting.isLoading ||
    subdistrict.isLoading ||
    supplier.isLoading ||
    taxCode.isReloading ||
    vendor.isLoading
  )

  useEffect(() => {
    if (vendor_id) {
      product.refetch(vendor_id)
    }

    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    company.fetchData().then((res) => {
      setAddress(res.address)

      if (res.subdistrict_id) {
        subdistrict.fetchData(res.subdistrict_id)
      }

      if (res.regency_id) {
        regency.fetchData(res.regency_id)
      }

      if (res.province_id) {
        province.fetchData(res.province_id)
      }

      if (res.country_id) {
        country.fetchData(res.country_id)
      }
    })

    // eslint-disable-next-line
  }, [])

  // Functions
  const getAddress = (): string => {
    let generateAddress = address ?? ""

    if (subdistrict.data) {
      generateAddress += ", " + subdistrict.data.subdistrict_name
    }

    if (regency.data) {
      generateAddress += ", " + regency.data.regency_name
    }

    if (province.data) {
      generateAddress += ", " + province.data.province_name
    }

    if (country.data) {
      generateAddress += ", " + country.data.country_name
    }

    return generateAddress
  }

  // Functions
  const getVendorAddress = (): string => {
    const data = supplier.data.find(item => item.value === vendor_id)

    let generateAddress = data?.address ?? ""

    if (data?.subdistrict_name) {
      generateAddress += ", " + data.subdistrict_name
    }

    if (data?.regency_name) {
      generateAddress += ", " + data.regency_name
    }

    if (data?.province_name) {
      generateAddress += ", " + data.province_name
    }

    if (data?.country_name) {
      generateAddress += ", " + data.country_name
    }

    return generateAddress
  }

  return (
    <>
      <CustomPrintHover
        onClick={(withImage) => {
          setWithImagePrint(withImage)
          toggle()
        }}
      />

      {isActive && (
        <PrintTransactionModal
          isLoading={isLoading}
          toggle={toggle}
        >
          <section className="flex flex-col gap-3">
            <div className="text-center text-2xl font-bold uppercase">
              {userToken?.company_name}
            </div>

            <section className="grid grid-cols-2 items-stretch gap-1">
              <section className="font-bold">
                <table className="table-top">
                  <tbody className="font-bold">
                    <tr>
                      <td className="pr-2">ADDRESS</td>
                      <td>: {getAddress()}</td>
                    </tr>

                    <tr>
                      <td>PHONE</td>
                      <td>
                        {" "}
                        : {setting.data?.phone ? setting.data?.phone : "-"}
                      </td>
                    </tr>

                    <tr>
                      <td>EMAIL</td>
                      <td>
                        {" "}
                        : {setting.data?.email ? setting.data?.email : "-"}
                      </td>
                    </tr>

                    <tr>
                      <td>WEBSITE</td>
                      <td>
                        : {setting.data?.website ? setting.data?.website : "-"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </section>

              <section className="flex flex-col justify-between items-end">
                <div className="text-2xl font-bold text-right">
                  REJECTED ITEM
                </div>

                <table>
                  <tbody className="font-bold ">
                    <tr>
                      <td width={10} className="text-right">
                        DATE :{" "}
                      </td>
                      <td className="whitespace-nowrap text-right">
                        {defaultValues?.transaction_date
                          ? moment(defaultValues?.transaction_date).format(
                            "DD/MM/YYYY"
                          )
                          : "-"}
                      </td>
                    </tr>

                    <tr>
                      <td width={10} className="whitespace-nowrap">
                        REJECTED ITEM NO :{" "}
                      </td>
                      <td className="whitespace-nowrap text-right">
                        {data.trx_code_reject}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </section>
            </section>

            <table className="table table-bordered">
              <tbody>
                <tr className="bg-black text-white font-bold">
                  <td colSpan={2}>VENDOR INFORMATION</td>
                </tr>

                <tr>
                  <td colSpan={2}>
                    <div>VENDOR NAME</div>
                    <div className="font-bold">{data.vendor_name}</div>
                  </td>
                </tr>

                <tr>
                  <td colSpan={2}>
                    <div>ADDRESS</div>
                    <div className="font-bold">{getVendorAddress()}</div>
                  </td>
                </tr>

                <tr>
                  <td>
                    <div>CONTACT NO</div>
                    <div className="font-bold">{vendor.data?.handphone}</div>
                  </td>

                  <td>
                    <div>EMAIL ADDRESS</div>
                    <div className="font-bold">{vendor.data?.email}</div>
                  </td>
                </tr>
              </tbody>
            </table>

            <table className="table table-bordered">
              <thead>
                <tr className="bg-black text-white">
                  <th>#</th>
                  {withImagePrint ? <th>PICTURE</th> : null}
                  <th>DESCRIPTION</th>
                  <th>BACK DELIVERED</th>
                  <th>QTY REJECTED</th>
                  <th>UNIT</th>
                  <th>PRICE</th>
                  <th>DISC</th>
                  <th>TOTAL</th>
                  <th>TAX</th>
                </tr>
              </thead>

              <tbody>
                {defaultValues?.prorejectdetail?.map((item, key) => {
                  // Vars
                  const productData = product.data.find(
                    (i) => i.value === item?.product_id?.toString()
                  )
                  const taxCodeData = taxCode.data.find(
                    (i) => i.value === item?.tax_id?.toString()
                  )
                  const imagePath = `${process.env.REACT_APP_BASE_URL_API}/getimage/?imgpath=${productData?.product_foto}`
                  const subtotal: number = (item?.price_reject ?? 0) * (item?.quantity_reject ?? 0)
                  const total: number = subtotal - ((item?.discount ?? 0) / 100 * subtotal)

                  return (
                    <tr>
                      <td className="text-center">{key + 1}</td>
                      {withImagePrint ? (
                        <td width={150}>
                          <img
                            width={150}
                            height={150}
                            src={imagePath}
                            alt={productData?.product_name}
                          />
                        </td>
                      ) : null}
                      <td>
                        <div>{productData?.product_barcode}</div>
                        <div className="mb-4">{productData?.product_name}</div>
                        <div>COLOUR : {productData?.mr_product_colour_name}</div>
                        <div>STYLE : {productData?.mr_product_style_name}</div>
                        <div>WEIGHT : {productData?.weight}</div>
                      </td>
                      <td className="text-right">
                        {convertNumber(item?.quantity_bill ?? 0).intoCurrency}
                      </td>
                      <td className="text-right">
                        {convertNumber(item?.quantity_reject ?? 0).intoCurrency}
                      </td>
                      <td>{productData?.mr_unit_name}</td>
                      <td className="text-right">
                        {convertNumber(item?.price_reject ?? 0).intoCurrency}
                      </td>
                      <td className="text-right">
                        {convertNumber(item?.discount ?? 0).intoCurrency}%
                      </td>
                      <td className="text-right">
                        {convertNumber(total).intoCurrency}
                      </td>
                      <td>{taxCodeData?.label}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            <section className="grid grid-cols-5 items-stretch gap-5">
              <section className="col-span-2">
                <table className="table table-bordered">
                  <tbody>
                    <tr className="bg-black text-white font-bold">
                      <td>Additional Notes:</td>
                    </tr>

                    <tr className="min-h-[300px]">
                      <td>{defaultValues?.memo}</td>
                    </tr>
                  </tbody>
                </table>
              </section>
              <div />

              <section className="col-span-2">
                <table className="w-full">
                  <tbody>
                    <tr className="font-bold">
                      <td>SUBTOTAL</td>
                      <td className="text-right border-b w-[100px]">
                        {convertNumber(subtotal).intoCurrency}
                      </td>
                    </tr>

                    <tr>
                      <td>TAX</td>
                      <td className="text-right border-b">
                        {convertNumber(tax).intoCurrency}
                      </td>
                    </tr>

                    <tr>
                      <td>TOTAL AMOUNT</td>
                      <td className="text-right border-b">
                        {convertNumber(total).intoCurrency}
                      </td>
                    </tr>

                    <tr>
                      <td>PAYMENT</td>
                      <td className="text-right border-b">
                        {convertNumber(0).intoCurrency}
                      </td>
                    </tr>

                    <tr className="font-bold">
                      <td>BALANCE</td>
                      <td className="text-right border-b border-double">
                        {convertNumber(total).intoCurrency}
                      </td>
                    </tr>

                    <tr className="bg-black text-white font-bold">
                      <td colSpan={3}>Authorized By</td>
                    </tr>

                    <tr className="h-[100px]" />

                    <tr>
                      <td colSpan={3}>{approvedBy.data?.employee_name}</td>
                    </tr>

                    <tr>
                      <td colSpan={3}>{approvedBy.data?.job_position_name}</td>
                    </tr>
                  </tbody>
                </table>
              </section>
            </section>
          </section>
        </PrintTransactionModal>
      )}
    </>
  )
}